// API客户端现在在theme.liquid中全局加载，无需动态加载

// ==== Cart ID Management (merged from cart-id.js) ====

// 1) 从 cookie 中取 token（在线商店会维护 cart=<token>）
function getCartTokenFromCookie() {
  const m = document.cookie.match(/(?:^|;\s*)cart=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

// 2) 清理 token，保留完整的 token 包括 ?key= 参数
function cleanCartToken(token) {
  if (!token) return null;
  // 保留完整的 token，包括 ?key= 参数，因为 Shopify 需要这个参数
  return token;
}

// 3) 由 token 推导 Storefront GraphQL 的全局 ID（GID）
//    规律：cart.id = base64("gid://shopify/Cart/" + <token>)
function buildCartIdFromToken(token) {
  if (!token) return null;
  // 清理 token，移除查询参数
  const cleanToken = cleanCartToken(token);
  return typeof btoa === 'function'
    ? btoa(`gid://shopify/Cart/${cleanToken}`)
    : null;
}

// 4) 尝试从 /cart.js 读取 token（老的 Ajax Cart 接口，主题常用）
async function getCartTokenFromAjax() {
  try {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) return null;
    const cart = await res.json();
    return cart && cart.token ? cart.token : null;
  } catch {
    return null;
  }
}

// 5) 兜底：让你的 App Proxy 去调用 Storefront API 的 cartCreate，返回新 id
async function createCartViaAppProxy() {
  try {
    const j = await apiClient.cartAction('ensure_cart', {});
    return j.ok ? j.cartId : null;
  } catch {
    return null;
  }
}

// 6) 暴露一个主函数：先读 sessionStorage，再一步步兜底
async function getStorefrontCartId() {
  // 优先使用缓存，但检查格式是否正确
  let id = sessionStorage.getItem('sf_cart_id');
  if (id) {
    // 验证缓存的 cartId 是否包含 ?key= 参数（新格式）
    try {
      const decoded = atob(id);
      if (decoded.includes('?key=')) {
        return id; // 新格式，直接返回
      } else {
        // 旧格式，清除缓存并重新生成
        sessionStorage.removeItem('sf_cart_id');
        console.error('Cleared old format cartId from cache');
      }
    } catch (e) {
      // 解码失败，清除缓存
      sessionStorage.removeItem('sf_cart_id');
      console.error('Cleared invalid cartId from cache');
    }
  }

  // cookie -> gid
  const cookieToken = getCartTokenFromCookie();
  id = buildCartIdFromToken(cookieToken);
  if (id) {
    sessionStorage.setItem('sf_cart_id', id);
    return id;
  }

  // /cart.js -> token -> gid
  const ajaxToken = await getCartTokenFromAjax();
  id = buildCartIdFromToken(ajaxToken);
  if (id) {
    sessionStorage.setItem('sf_cart_id', id);
    return id;
  }

  // 创建一个新的 cart（会设置 cart cookie），并缓存
  id = await createCartViaAppProxy();
  if (id) {
    sessionStorage.setItem('sf_cart_id', id);
    return id;
  }

  // 全部失败
  return null;
}

// 将函数暴露到全局作用域，以便其他脚本可以使用
if (typeof window !== 'undefined') {
  window.getStorefrontCartId = getStorefrontCartId;
}

// 同时支持CommonJS模块（如果环境支持）
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { getStorefrontCartId };
}

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', async (event) => {
      event.preventDefault();
      const cartItems =
        this.closest('cart-items') || this.closest('cart-drawer-items');
      await cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') ||
      document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce(async (event) => {
      await this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (event.source === 'cart-items') {
          return;
        }
        return this.onCartUpdate();
      },
    );
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  async validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace(
        '[min]',
        event.target.dataset.min,
      );
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace(
        '[max]',
        event.target.max,
      );
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace(
        '[step]',
        event.target.step,
      );
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      await this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId,
      );
    }
  }

  async onChange(event) {
    await this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(
            responseText,
            'text/html',
          );
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(
            responseText,
            'text/html',
          );
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  async updateQuantity(line, quantity, event, name, variantId) {
    this.enableLoading(line);

    // 如果移除商品（数量为0），先检查是否有关联的服务需要一起移除
    if (quantity === 0) {
      await this.removeAssociatedServices(line);
    }

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
    const eventTarget =
      event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        CartPerformance.measure(
          `${eventTarget}:paint-updated-sections"`,
          () => {
            const quantityElement =
              document.getElementById(`Quantity-${line}`) ||
              document.getElementById(`Drawer-quantity-${line}`);
            const items = document.querySelectorAll('.cart-item');

            if (parsedState.errors) {
              quantityElement.value = quantityElement.getAttribute('value');
              this.updateLiveRegions(line, parsedState.errors);
              return;
            }

            this.classList.toggle('is-empty', parsedState.item_count === 0);
            const cartDrawerWrapper = document.querySelector('cart-drawer');
            const cartFooter = document.getElementById('main-cart-footer');

            if (cartFooter)
              cartFooter.classList.toggle(
                'is-empty',
                parsedState.item_count === 0,
              );
            if (cartDrawerWrapper)
              cartDrawerWrapper.classList.toggle(
                'is-empty',
                parsedState.item_count === 0,
              );

            this.getSectionsToRender().forEach((section) => {
              const elementToReplace =
                document
                  .getElementById(section.id)
                  .querySelector(section.selector) ||
                document.getElementById(section.id);
              elementToReplace.innerHTML = this.getSectionInnerHTML(
                parsedState.sections[section.section],
                section.selector,
              );
            });
            const updatedValue = parsedState.items[line - 1]
              ? parsedState.items[line - 1].quantity
              : undefined;
            let message = '';
            if (
              items.length === parsedState.items.length &&
              updatedValue !== parseInt(quantityElement.value)
            ) {
              if (typeof updatedValue === 'undefined') {
                message = window.cartStrings.error;
              } else {
                message = window.cartStrings.quantityError.replace(
                  '[quantity]',
                  updatedValue,
                );
              }
            }
            this.updateLiveRegions(line, message);

            const lineItem =
              document.getElementById(`CartItem-${line}`) ||
              document.getElementById(`CartDrawer-Item-${line}`);
            if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
              cartDrawerWrapper
                ? trapFocus(
                    cartDrawerWrapper,
                    lineItem.querySelector(`[name="${name}"]`),
                  )
                : lineItem.querySelector(`[name="${name}"]`).focus();
            } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
              trapFocus(
                cartDrawerWrapper.querySelector('.drawer__inner-empty'),
                cartDrawerWrapper.querySelector('a'),
              );
            } else if (
              document.querySelector('.cart-item') &&
              cartDrawerWrapper
            ) {
              trapFocus(
                cartDrawerWrapper,
                document.querySelector('.cart-item__name'),
              );
            }
          },
        );

        CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-items',
          cartData: parsedState,
          variantId: variantId,
        });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) =>
          overlay.classList.add('hidden'),
        );
        const errors =
          document.getElementById('cart-errors') ||
          document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError)
      lineItemError.querySelector('.cart-item__error-text').textContent =
        message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') ||
      document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems =
      document.getElementById('main-cart-items') ||
      document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading__spinner`,
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading__spinner`,
    );

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) =>
      overlay.classList.remove('hidden'),
    );

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems =
      document.getElementById('main-cart-items') ||
      document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading__spinner`,
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading__spinner`,
    );

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) =>
      overlay.classList.add('hidden'),
    );
  }

  async removeAssociatedServices(baseLineIndex) {
    try {
      // 获取当前购物车状态以获取variant ID
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();

      // 找到要移除的基础商品
      const baseItem = cartData.items[baseLineIndex - 1]; // line index 是从1开始的
      if (!baseItem) {
        console.error('Base item not found for line index:', baseLineIndex);
        return;
      }

      // 尝试使用后端API处理移除
      try {
        // 获取cartId
        // 使用全局函数获取cartId（现在已合并到cart-manager.js中）
        let cartId = null;
        if (typeof window.getStorefrontCartId === 'function') {
          cartId = await window.getStorefrontCartId();
        } else {
          // 回退方案：内联实现
          cartId = await this.getStorefrontCartIdFallback();
        }
        if (cartId) {
          const result = await apiClient.removeProduct(
            cartId,
            baseItem.variant_id,
          );

          if (result.ok) {
            return; // 成功，不需要前端处理
          }
        }
      } catch (backendError) {
        console.error(
          'Backend API removal failed, using frontend fallback:',
          backendError,
        );
      }

      // 前端回退逻辑：直接移除商品，不处理服务绑定
      // 这里不处理服务移除，因为后端API失败时，我们只移除主商品
      // 服务绑定的处理应该由后端来保证
    } catch (error) {
      console.error('Failed to remove associated services:', error);
    }
  }

  // 回退方案：内联的cartId获取实现
  async getStorefrontCartIdFallback() {
    try {
      // 尝试从sessionStorage获取
      let cartId = sessionStorage.getItem('sf_cart_id');
      if (cartId) {
        return cartId;
      }

      // 尝试从cookie获取
      const cookieMatch = document.cookie.match(/(?:^|;\s*)cart=([^;]+)/);
      if (cookieMatch) {
        const token = decodeURIComponent(cookieMatch[1]);
        cartId = btoa(`gid://shopify/Cart/${token}`);
        sessionStorage.setItem('sf_cart_id', cartId);
        return cartId;
      }

      // 尝试从/cart.js获取
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();
      if (cartData.token) {
        cartId = btoa(`gid://shopify/Cart/${cartData.token}`);
        sessionStorage.setItem('sf_cart_id', cartId);
        return cartId;
      }

      return null;
    } catch (error) {
      console.error('Failed to get Storefront Cart ID (fallback):', error);
      return null;
    }
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, {
              ...fetchConfig(),
              ...{ body },
            }).then(() =>
              CartPerformance.measureFromEvent(
                'note-update:user-action',
                event,
              ),
            );
          }, ON_CHANGE_DEBOUNCE_TIMER),
        );
      }
    },
  );
}
